import React, { useState, useMemo } from "react";
import { Layers, Plus, Search, Filter, MoreVertical, Edit2, Ban, Trash2, CheckCircle2, Eye, Link, Users, BookOpen } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";

const GlassCard = ({ children, className = "" }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

// --- Dummy Data ---
const DUMMY_TRACKS = [
  { id: "TRK-001", title: "Fullstack UI/UX Career Track", coursesCount: 6, status: "Active", enrollments: 850, price: "₦150,000" },
  { id: "TRK-002", title: "Frontend Engineering Bootcamp", coursesCount: 8, status: "Active", enrollments: 1240, price: "₦200,000" },
  { id: "TRK-003", title: "Data Science Mastery", coursesCount: 5, status: "Draft", enrollments: 0, price: "₦180,000" },
  { id: "TRK-004", title: "Digital Marketing Professional", coursesCount: 4, status: "Active", enrollments: 620, price: "₦120,000" },
  { id: "TRK-005", title: "Cloud Computing Expert", coursesCount: 7, status: "Suspended", enrollments: 45, price: "₦250,000" },
];

const AdminTracks = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("All Status");
  const [activeDropdown, setActiveDropdown] = useState(null);

  const filteredTracks = useMemo(() => {
    return DUMMY_TRACKS.filter(track => {
      const matchesSearch = track.title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === "All Status" || track.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, filterStatus]);

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">Career Tracks</h1>
          <p className="cht-text-gray-500 cht-font-medium">Design and manage multi-course career paths and curriculum bundles.</p>
        </div>
        <Button variant="primary" leftIcon={<Plus size={18} />} className="cht-rounded-2xl">
          Create Track
        </Button>
      </header>

      {/* KPI Grid */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-6">
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-purple-50 cht-text-purple-600 cht-rounded-2xl"><Layers size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Total Tracks</p><h4 className="cht-text-2xl cht-font-black">12</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl"><CheckCircle2 size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Active</p><h4 className="cht-text-2xl cht-font-black">8</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-blue-50 cht-text-blue-600 cht-rounded-2xl"><Link size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Avg Courses/Track</p><h4 className="cht-text-2xl cht-font-black">5.4</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-amber-50 cht-text-amber-600 cht-rounded-2xl"><Users size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Track Enrollments</p><h4 className="cht-text-2xl cht-font-black">2.7K</h4></div>
        </GlassCard>
      </div>

      {/* Filter Bar */}
      <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-50 cht-shadow-sm">
        <div className="cht-relative cht-flex-1 cht-w-full">
          <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Search career tracks..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3.5 cht-pl-12 cht-pr-4 cht-text-sm cht-font-bold focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
          />
        </div>
        <div className="cht-flex cht-items-center cht-gap-2 cht-w-full md:cht-w-auto">
          {["All Status", "Active", "Draft", "Suspended"].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`cht-flex-1 md:cht-flex-none cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${
                filterStatus === status ? "cht-bg-[#1e293b] cht-text-white" : "cht-bg-gray-50 cht-text-gray-400 hover:cht-bg-gray-100"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Tracks Table */}
      <GlassCard className="cht-rounded-[40px] cht-overflow-visible">
        <div className="cht-overflow-x-auto">
          <table className="cht-w-full">
            <thead>
              <tr className="cht-border-b cht-border-gray-50">
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Track Details</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Bundle Size</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Status</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Performance</th>
                <th className="cht-px-8 cht-py-6 cht-text-right cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="cht-divide-y cht-divide-gray-50">
              {filteredTracks.map((track) => (
                <tr key={track.id} className="hover:cht-bg-gray-50/50 cht-transition-colors group">
                  <td className="cht-px-8 cht-py-6">
                    <div className="cht-space-y-1">
                      <h4 className="cht-text-sm cht-font-black cht-text-gray-900 cht-line-clamp-1">{track.title}</h4>
                      <p className="cht-text-xs cht-text-gray-400 cht-font-medium">ID: {track.id}</p>
                    </div>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <div className="cht-flex cht-items-center cht-gap-2">
                      <div className="cht-w-8 cht-h-8 cht-rounded-lg cht-bg-purple-50 cht-text-purple-600 cht-flex cht-items-center cht-justify-center">
                        <BookOpen size={14} />
                      </div>
                      <span className="cht-text-sm cht-font-bold cht-text-gray-900">{track.coursesCount} Courses</span>
                    </div>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <span className={`cht-px-3 cht-py-1.5 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest ${
                      track.status === 'Active' ? 'cht-bg-emerald-100 cht-text-emerald-700' :
                      track.status === 'Suspended' ? 'cht-bg-rose-100 cht-text-rose-700' :
                      'cht-bg-gray-100 cht-text-gray-600'
                    }`}>
                      {track.status}
                    </span>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <div className="cht-space-y-1">
                      <p className="cht-text-xs cht-font-black cht-text-gray-900">{track.price}</p>
                      <p className="cht-text-[10px] cht-text-gray-400 cht-font-bold">{track.enrollments} Enrolled</p>
                    </div>
                  </td>
                  <td className="cht-px-8 cht-py-6 cht-text-right">
                    <div className="cht-relative cht-inline-block">
                      <button 
                        onClick={() => setActiveDropdown(activeDropdown === track.id ? null : track.id)}
                        className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-text-gray-400 hover:cht-bg-gray-100 hover:cht-text-gray-900 transition-colors"
                      >
                        <MoreVertical size={20} />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === track.id && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            className="cht-absolute cht-right-0 cht-mt-2 cht-w-48 cht-bg-white cht-rounded-2xl cht-shadow-[0_20px_60px_rgba(0,0,0,0.1)] cht-border cht-border-gray-50 cht-p-2 cht-z-[100]"
                          >
                            <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-rounded-xl transition-colors">
                              <Eye size={16} /> View Curriculum
                            </button>
                            <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-rounded-xl transition-colors">
                              <Edit2 size={16} /> Edit Track
                            </button>
                            <div className="cht-h-px cht-bg-gray-100 cht-my-1" />
                            {track.status === 'Active' ? (
                              <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-amber-600 hover:cht-bg-amber-50 cht-rounded-xl transition-colors">
                                <Ban size={16} /> Suspend Track
                              </button>
                            ) : (
                              <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-emerald-600 hover:cht-bg-emerald-50 cht-rounded-xl transition-colors">
                                <CheckCircle2 size={16} /> Reactivate
                              </button>
                            )}
                            <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-xl transition-colors">
                              <Trash2 size={16} /> Delete Bundle
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredTracks.length === 0 && (
            <div className="cht-py-20 cht-text-center">
              <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-mx-auto cht-mb-4 cht-text-gray-300">
                <Layers size={32} />
              </div>
              <h3 className="cht-text-lg cht-font-black cht-text-gray-900">No tracks found</h3>
              <p className="cht-text-sm cht-text-gray-400 cht-font-medium">Try adjusting your filters or search terms.</p>
            </div>
          )}
        </div>
      </GlassCard>

      {activeDropdown && (
        <div 
          className="cht-fixed cht-inset-0 cht-z-[50]" 
          onClick={() => setActiveDropdown(null)} 
        />
      )}
    </div>
  );
};

export default AdminTracks;
